<template>
    <spinner-until :condition="participantLoaded">
        <participant-menu>
            <div class="my-metrics">
                <div>
                    <b-row>
                        <b-col cols="12" ref="my-logs">
                            <b-card no-body>
                                <b-card-header v-if="!showParticipantMenuAsAccordion">
                                    <h5>
                                        <span class="icon-wrapper icon-wrapper-left">
                                            <shs-icon name="graph"></shs-icon>
                                        </span>
                                        {{ $i18n.gt('HEADING_MY_METRICS') }}
                                        <print-button content="my-logs" :refs="$refs"/>
                                    </h5>
                                </b-card-header>
                                <b-card-body class="light-bg participant-health-numbers__graphs equal-height">

                                    <b-btn @click="manualEntryClick" class="mb-3">{{
                                            $i18n.gt('LABEL_METRICS_MANUAL_ENTRY')
                                        }}
                                    </b-btn>
                                    <b-btn @click="addGoalClick" class="mb-3 ml-2">
                                        {{ $i18n.gt('LABEL_METRICS_ADD_GOALS') }}
                                    </b-btn>

                                    <!-- Date range -->
                                    <range-datepicker @input="dateChanged"/>

                                    <!-- Settings menu -->
                                    <div class="float-right mb-3 settings-menu"
                                         v-if="availableMetrics">
                                        <b-dropdown right :text="$i18n.gt('LABEL_METRICS_SETTINGS')">
                                            <b-dropdown-item v-if="hasFitbit"
                                                             @click="disconnectFromFitbit()">
                                                <img src="/../../images/fitbit-logo.png" alt="Fitbit">
                                                {{ $i18n.gt('LABEL_DISCONNECT_FITBIT') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item v-else
                                                             @click="connectToFitbit()">
                                                <img src="/../../images/fitbit-logo.png" alt="Fitbit">
                                                {{ $i18n.gt('LABEL_CONNECT_FITBIT') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item v-if="hasAppleHealth">
                                                <img src="/../../images/apple-health-logo.png" alt="Apple Health">
                                                {{ $i18n.gt('LABEL_APPLE_HEALTH_CONNECTED') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item v-else
                                                             @click="$bvModal.show('apple-health-modal')">
                                                <img src="/../../images/apple-health-logo.png" alt="Apple Health">
                                                {{ $i18n.gt('LABEL_CONNECT_APPLE_HEALTH') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item v-if="hasGoogleFit"
                                                             class="google-fit"
                                                             @click="disconnectFromGoogleFit()">
                                                <img src="/../../images/google-logo.png" alt="Google">
                                                {{ $i18n.gt('LABEL_DISCONNECT_GOOGLE_FIT') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item v-else
                                                             class="google-fit"
                                                             @click="connectToGoogleFit()">
                                                <img src="/../../images/google-logo.png" alt="Google">
                                                {{ $i18n.gt('LABEL_CONNECT_GOOGLE_FIT') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item @click="refreshData()">
                                                <img src="/../../images/refresh-icon.png" alt="Refresh">
                                                {{ $i18n.gt('LABEL_REFRESH_DATA') }}
                                            </b-dropdown-item>
                                        </b-dropdown>
                                    </div>
                                    <div style="clear:both"></div>

                                    <!-- FITBIT CHARTS -->

                                    <b-container
                                        class="d-flex flex-wrap justify-content-md-between flex-column flex-md-row p-0"
                                        v-if="availableMetrics">

                                        <b-card class="graph-block" no-body
                                                v-for="(met, index) in availableMetrics"
                                                :key="index">
                                            <b-card-header>
                                                <h5>{{ metricUtil.metricLabels()[met] }}</h5>
                                            </b-card-header>
                                            <b-card-body class="p-0 m-0">
                                                <participant-metrics-chart-time-series
                                                    v-if="met != 'BLOOD_PRESSURE'"
                                                    :metrics="participant.metrics"
                                                    :highlight-start="participant.last_rpm_check_date"
                                                    :metric="met"
                                                    :dateRange="dateRange">
                                                </participant-metrics-chart-time-series>
                                                <participant-metrics-chart-box-plot
                                                    v-else
                                                    :metrics="participant.metrics"
                                                    :highlight-start="participant.last_rpm_check_date"
                                                    :metricTop="'SYSTOLIC'"
                                                    :metricBottom="'DIASTOLIC'"
                                                    :dateRange="dateRange">
                                                </participant-metrics-chart-box-plot>
                                            </b-card-body>
                                        </b-card>

                                    </b-container>


                                    <div v-else>
                                        <!-- not connected or connecting - show instructions -->
                                        <b-alert show variant="info">
                                            {{ $i18n.gt('LABEL_METRICS_CONNECTION_HELP') }}
                                            <br/>
                                            <div class="text-center mt-3">
                                                <b-btn @click="connectToFitbit()">
                                                    <img src="/../../images/fitbit-logo.png" alt="Fitbit">
                                                    {{ $i18n.gt('LABEL_CONNECT_FITBIT') }}
                                                </b-btn>
                                            </div>
                                            <br/>
                                            <div class="text-center mt-3">
                                                <b-btn @click="connectToGoogleFit()" class="google-fit">
                                                    <img src="/../../images/google-logo.png" alt="Google">
                                                    {{ $i18n.gt('LABEL_CONNECT_GOOGLE_FIT') }}
                                                </b-btn>
                                            </div>
                                        </b-alert>

                                    </div>

                                    <div v-if="handlingConnectToFitbit">
                                        <b-alert show variant="info">
                                            {{ $i18n.gt('LABEL_RETRIEVING_FITBIT_DATA') }}
                                        </b-alert>
                                    </div>
                                    <div v-if="handlingConnectToGoogleFit">
                                        <b-alert show variant="info">
                                            {{ $i18n.gt('LABEL_RETRIEVING_GOOGLE_FIT_DATA') }}
                                        </b-alert>
                                    </div>

                                </b-card-body>
                            </b-card>
                        </b-col>
                    </b-row>

                </div>

                <!-- Metric Manual Entries -->
                <metric-entry-modal
                    :title="$i18n.gt('HEADING_MY_LOGS_MANUAL_ENTRY')"
                    :onSubmit="manualEntrySubmit"
                    :metrics="participant.metrics"
                    service="MANUAL"
                    modal-id="manualEntryModal">
                </metric-entry-modal>

                <!-- Goal Entry for RPM Thresholds -->
                <metric-goal-modal
                    :title="$i18n.gt('HEADING_MY_LOGS_ADD_GOALS')"
                    :onSubmit="addGoalSubmit"
                    :metrics="participant.metrics"
                    service="GOAL"
                    modal-id="addGoalModal">
                </metric-goal-modal>

                <!--            Modal to show instructions for connecting to apple health-->
                <b-modal
                    :title="$i18n.gt('HEADING_APPLE_HEALTH_MODAL')"
                    id="apple-health-modal">
                    <b-alert show variant="info">
                        {{ $i18n.gt('LABEL_APPLE_HEALTH_HELP') }}
                    </b-alert>

                </b-modal>
            </div>
        </participant-menu>
    </spinner-until>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

import Vue from 'vue'
import PatientPageMixin from '@/providers/PatientPageMixin'
import MetricFields from '@/models/Metric'
import MetricUtil from '@/providers/Metrics'
import * as _ from 'lodash'
import SpinnerUntil from "@/components/SpinnerUntil";
import RangeDatepicker from "@/components/RangeDatepicker.vue";

/**
 * Page to display a Participant's wearables metrics
 */
export default Vue.extend({
    name: 'MyMetrics',
    components: {SpinnerUntil, RangeDatepicker},
    mixins: [PatientPageMixin],
    data() {
        return {
            handlingConnectToFitbit: false,
            handlingConnectToGoogleFit: false,

            metricFields: MetricFields,
            fieldGroups: [['metric', 'date', 'value']],
            manualEntryModel: {
                metric: null, date: null, value: null
            },
            metricUtil: MetricUtil,
            dateRange: null,
        }
    },
    computed: {
        hasFitbit() {
            return this.participant ? !!this.participant.fitbit_access_token : false
        },
        fitbitCode() {
            // url param indicating we have just done a successful Fitbit authorization
            if (this.$router.currentRoute.path === "/metrics") {
                return this.$route.query.code
            } else {
                return null;
            }
        },
        hasGoogleFit() {
            return this.participant ? !!this.participant.google_fit_access_token : false
        },
        googleFitCode() {
            // url param indicating we have just done a successful Google Fit authorization
            if (this.$router.currentRoute.path === "/metricsGoogleFit") {
                return this.$route.query.code
            } else {
                return null;
            }
        },
        hasAppleHealth() {
            return _.reduce(this.participant.metrics, (item, metric) => {
                return item === true || metric.service === "APPLE"
            }, false)
        },
        availableMetrics() {
            return MetricUtil.availableMetrics(this.participant.metrics)
        }
    },
    mounted() {
        //Fitbitcode and Googlefit code come from the URL when being redirected by google or fitbit
        //WAIT until we have finished the connection until loading the participant
        //If no connect code is present, load participant right away
        if (this.fitbitCode) {
            this.handlingConnectToFitbit = true
            this.$api.handleConnectToFitbitCode(this.$user.participantId(), this.fitbitCode)
                .then(() => {
                    this.loadParticipant(true)
                })
                .catch(() => {
                    alert('Error connecting to Fitbit')
                    this.handlingConnectToFitbit = false
                })
        } else if (this.googleFitCode) {
            this.handlingConnectToGoogleFit = true
            this.$api.handleConnectToGoogleFitCode(this.$user.participantId(), this.googleFitCode)
                .then(() => {
                    this.loadParticipant(true)
                })
                .catch(() => {
                    alert('Error connecting to Google fit')
                    this.handlingConnectToGoogleFit = false
                })
        } else {
            this.loadParticipant(true)
        }
    },
    watch: {
        // NB: we use this watch to turn off the handlingConnectToFitbit state and reload the page,
        // because the loadParticipant call above does not return a proper promise that would let us
        // detect when the participant is reloaded and the token is set - the result is a brief flash of
        // the 'you must connect' alert before metrics are displayed (???)
        'participant.fitbit_access_token': {
            handler: function (newVal, oldVal) {
                if (newVal) {
                    this.handlingConnectToFitbit = false
                    this.$router.replace('/metrics') // clear URL
                }
            }
        },
        'participant.google_fit_access_token': {
            handler: function (newVal, oldVal) {
                if (newVal) {
                    this.handlingConnectToGoogleFit = false
                    this.$router.replace('/metrics') // clear URL
                }
            }
        }
    },
    methods: {
        connectToFitbit() {
            this.$api.getFitbitAuthUrl()
                .then((response) => {
                    window.location = response.data
                    this.refreshData()
                })
        },
        refreshData() {
            if (this.hasFitbit) {
                this.$api.refreshFitbitData(this.$user.participantId()).then(() => {
                    this.loadParticipant(true)
                }).catch((response) => {
                    alert(response.body.message)
                })
            }
            if (this.hasGoogleFit) {
                this.$api.refreshGoogleFitData(this.$user.participantId()).then(() => {
                    this.loadParticipant(true)
                }).catch((response) => {
                    alert(response.body.message)
                })
            }

        },
        disconnectFromFitbit() {
            this.$api.disconnectFromFitbit(this.$user.participantId())
                .then(() => {
                    this.loadParticipant(true) // force
                })
                .catch((response) => {
                    alert(response.body.message)
                })
        },
        connectToGoogleFit() {
            this.$api.getGoogleFitAuthUrl()
                .then((response) => {
                    window.location = response.data
                    this.refreshData()
                })
        },
        disconnectFromGoogleFit() {
            this.$api.disconnectFromGoogleFit(this.$user.participantId())
                .then(() => {
                    this.loadParticipant(true) // force
                })
                .catch((response) => {
                    alert(response.body.message)
                })
        },
        manualEntryClick() {
            this.$bvModal.show('manualEntryModal')
        },
        addGoalClick() {
            this.$bvModal.show('addGoalModal')
        },
        async manualEntrySubmit(entries) {
            await this.$api.postMetricManualEntry(this.participant.id, entries)
            this.refreshData()
        },
        async addGoalSubmit(entries) {
            await this.$api.postMetricGoals(this.participant.id, entries)
            this.refreshData()
        },
        dateChanged(dateRange) {
            this.dateRange = dateRange
        }
    }
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

.graph-block {
    width: 100%;
}

@media (min-width: 960px) {
    .graph-block {
        width: 49%;
    }
}

.participant-health-numbers__graphs .graph-block {
    margin: 1em auto;
}

.settings-menu {
    a {
        padding-top: 6px;
        padding-bottom: 6px;

        img {
            width: 1.5rem;
            height: 1.5rem;
            margin-right: 0.6rem;
        }

        &:after {
            content: "\00a0 \00a0 ";
        }
    }

    .google-fit {
        font-family: 'Roboto', sans-serif;
    }
}

.my-metrics {
    .print-button {
        float: right;
        margin-top: .2em;
    }
}

@media print {
    ul.dropdown-menu, ul.dropdown-menu * {
        display: none !important;
    }
}

</style>
